import React, { useState, useEffect } from 'react';
import { Home, Layers, Mail, Menu, X, Phone, MapPin, Shield, Zap, Image as ImageIcon, CheckCircle, Wind, Facebook } from 'lucide-react'; // Added social media icons


const IconWrapper = ({ icon: Icon, className = "w-6 h-6" }) => <Icon className={className} />;

const NavLink = ({ page, currentPage, setCurrentPage, children, setMenuOpen }) => (
  <a
    href={`#${page.toLowerCase().replace(/\s+/g, '-')}`}
    onClick={(e) => {
      e.preventDefault();
      setCurrentPage(page);
      if (setMenuOpen) setMenuOpen(false);
    }}
    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ease-in-out
      ${currentPage === page
        ? 'bg-sky-600 text-white shadow-md'
        : 'text-gray-700 hover:bg-sky-100 hover:text-sky-700'
      }`}
  >
    {children}
  </a>
);

const Section = ({ id, title, children, className = "" }) => (
  <section id={id.toLowerCase().replace(/\s+/g, '-')} className={`py-12 md:py-20 px-4 sm:px-6 lg:px-8 ${className}`}>
    <div className="max-w-4xl mx-auto">
      {title && <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-10 md:mb-16">{title}</h2>}
      {children}
    </div>
  </section>
);

const Card = ({ icon, title, description, children }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out h-full flex flex-col">
    {icon && <div className="text-sky-500 mb-4">{icon}</div>}
    <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm flex-grow">{description}</p>
    {children && <div className="mt-4">{children}</div>}
  </div>
);

const Button = ({ children, onClick, type = "button", className = "", variant = "primary" }) => {
  const baseStyles = "px-6 py-3 rounded-lg font-semibold transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 text-sm md:text-base";
  const primaryStyles = "bg-sky-500 text-white hover:bg-sky-600 focus:ring-sky-500 shadow hover:shadow-md";
  const secondaryStyles = "bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400";

  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseStyles} ${variant === 'primary' ? primaryStyles : secondaryStyles} ${className}`}
    >
      {children}
    </button>
  );
};

// --- Page Components ---

const HomePage = ({ setCurrentPage }) => (
  <Section id="inicio" className="bg-gradient-to-br from-sky-50 to-indigo-50 min-h-[calc(100vh-4rem)] flex items-center">
    <div className="text-center max-w-3xl mx-auto">
      
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-800 mb-6">
        <span className="block">Vidriería Peña</span>
        <span className="block text-sky-600">Soluciones en Vidrio y Aluminio en Chiquinquirá</span>
      </h1>
      <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
        Transformamos tus espacios en Chiquinquirá y la región con diseños elegantes y funcionales en vidrio y aluminio. Calidad, precisión y servicio excepcional para cada proyecto al mejor Precio.
      </p>
      <div className="space-x-0 space-y-4 sm:space-x-4 sm:space-y-0">
        <Button onClick={() => setCurrentPage('Servicios')} className="w-full sm:w-auto">
          Nuestros Servicios
        </Button>
        <Button onClick={() => setCurrentPage('Contacto')} variant="secondary" className="w-full sm:w-auto">
          Contáctanos
        </Button>
      </div>
    </div>
  </Section>
);

const ServicesPage = () => {
  const services = [
    {
      icon: <IconWrapper icon={Wind} className="w-10 h-10" />,
      title: "Ventanas y Puertas de Vidrio en Chiquinquirá",
      description: "Instalación y diseño de ventanas y puertas de vidrio templado, laminado o insulado, con perfilería de aluminio de alta calidad. Atendemos en Chiquinquirá y municipios cercanos."
    },
    {
      icon: <IconWrapper icon={Shield} className="w-10 h-10" />,
      title: "Divisiones de Baño y Oficina",
      description: "Mamparas de baño elegantes y funcionales. Divisiones de vidrio para oficinas que optimizan el espacio y la luz."
    },
    {
      icon: <IconWrapper icon={ImageIcon} className="w-10 h-10" />,
      title: "Espejos Decorativos y a Medida",
      description: "Fabricamos e instalamos espejos biselados, con luz LED, para gimnasios, baños y cualquier espacio que desees realzar."
    },
    {
      icon: <IconWrapper icon={Layers} className="w-10 h-10" />,
      title: "Vitrinas y Exhibidores Comerciales",
      description: "Diseño y montaje de vitrinas y exhibidores de vidrio para locales comerciales, realzando la presentación de tus productos."
    },
    {
      icon: <IconWrapper icon={Zap} className="w-10 h-10" />,
      title: "Reparaciones e Instalaciones Rápidas",
      description: "Servicio ágil para reparación de vidrios rotos y mantenimiento general de tus instalaciones de vidrio y aluminio."
    },
    {
      icon: <IconWrapper icon={CheckCircle} className="w-10 h-10" />,
      title: "Proyectos Personalizados en Vidrio",
      description: "Trabajamos contigo para materializar tus ideas en vidrio, desde barandales hasta fachadas y soluciones arquitectónicas innovadoras."
    }
  ];

  return (
    <Section id="servicios" title="Nuestros Servicios en Vidriería Peña - Chiquinquirá">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map(service => (
          <Card key={service.title} icon={service.icon} title={service.title} description={service.description} />
        ))}
      </div>
    </Section>
  );
};

const ProductsPage = () => {
  const products = [
    {
      name: "Vidrio Templado de Seguridad",
      description: "Alta resistencia a impactos. Ideal para seguridad en hogares y negocios en Chiquinquirá.",
      // Ejemplo: Busca en Unsplash "tempered glass window" o "vidrio templado seguridad"
      imgPlaceholder: "https://www.amacal.com/wp-content/uploads/2018/11/cristal-templado.png.webp" 
    },
    {
      name: "Vidrio Laminado de Seguridad",
      description: "Mayor seguridad y aislamiento acústico para tus espacios en Chiquinquirá.",
      // Ejemplo: Busca en Unsplash "laminated safety glass" o "vidrio laminado ventana"
      imgPlaceholder: "https://climalit.es/blog/wp-content/uploads/2015/12/vidrio_laminado-1280x521.jpg"
    },
    {
      name: "Vidrio Crudo ",
      description: "Aquel que no ha sido sometido a ningún proceso térmico posterior a su fabricación en un horno float. Esto significa que no ha sido templado, laminado o sometido a ningún tratamiento que mejore su resistencia o seguridad.",
      // Ejemplo: Busca en Unsplash "double glazed window" o "vidrio termoacustico"
      imgPlaceholder: "https://www.vicayseg.com/wp-content/uploads/2019/07/vidrio-crudo.png"
    },
    {
      name: "Espejos Biselados y Decorativos",
      description: "Con bordes pulidos para un acabado elegante. Diseños a medida para Chiquinquirá.",
      // Ejemplo: Busca en Unsplash "decorative mirror bathroom" o "espejo biselado elegante"
      imgPlaceholder: "https://www.cristalesfayenza.com/wp-content/uploads/2024/07/Shape_01.jpg"
    },
    {
      name: "Perfilería de Aluminio Resistente",
      description: "Variedad de perfiles para ventanas, puertas y estructuras. Acabados duraderos para Chiquinquirá.",
      // Ejemplo: Busca en Unsplash "aluminum window frame" o "perfiles de aluminio construccion"
      imgPlaceholder: "https://www.perfilesdealuminiofaga.com/wp-content/uploads/2021/05/perfiles-de-aluminio-faga-local-031.jpg"
    },
    {
      name: "Herrajes y Accesorios de Calidad",
      description: "Componentes de alta calidad para instalaciones duraderas y funcionales en Chiquinquirá.",
      // Ejemplo: Busca en Unsplash "window hardware" o "herrajes para vidrio"
      imgPlaceholder: "https://metriatools.com/wp-content/uploads/2024/01/image-20.jpeg"
    }
  ];

  return (
    <Section id="productos" title="Catálogo de Productos - Vidriería Peña Chiquinquirá">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map(product => (
          <div key={product.name} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <img 
              src={product.imgPlaceholder} 
              alt={`Imagen de ${product.name} - Vidriería Peña en Chiquinquirá`} 
              className="w-full h-56 object-cover"
              onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/600x400/CBD5E1/475569?text=Imagen+No+Disponible"; }}
            />
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{product.name}</h3>
              <p className="text-gray-600 text-sm">{product.description}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);
    
    console.log("Form data submitted:", formData);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500)); 
    setSubmitMessage({ type: 'success', text: '¡Mensaje enviado (simulado)! Nos pondremos en contacto pronto.' });
    setFormData({ name: '', email: '', message: '' });
    setIsSubmitting(false);
  };

  return (
    <Section id="contacto" title="Ponte en Contacto con Vidriería Peña en Chiquinquirá">
      <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Información de Contacto en Chiquinquirá</h3>
            <p className="text-gray-600 flex items-center mb-2">
              <Phone className="w-5 h-5 mr-3 text-sky-500" /> (COL) 311 3503842
            </p>
            <p className="text-gray-600 flex items-center mb-2">
              <Mail className="w-5 h-5 mr-3 text-sky-500" /> vidalpechiquinquira@gmail.com
            </p>
            <p className="text-gray-600 flex items-center">
              <MapPin className="w-5 h-5 mr-3 text-sky-500" /> calle 4b #7 110, Chiquinquirá, Boyacá
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Horario de Atención</h3>
            <p className="text-gray-600">Lunes a Viernes: 9:00 AM - 5:30 PM</p>
            <p className="text-gray-600">Sábados: 9:00 AM - 2:00 PM</p>
          </div>
          <div className="bg-gray-200 rounded-lg h-64 flex items-center justify-center text-gray-500">
            <MapPin className="w-12 h-12 mr-2" />
            <span>Ubicación en Mapa de Chiquinquirá (Placeholder)</span>
          </div>
        </div>

        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-xl">
          <h3 className="text-2xl font-semibold text-gray-800 mb-6">Envíanos un Mensaje</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
              <input type="text" name="name" id="name" required value={formData.name} onChange={handleChange}
                     className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500 transition-colors" />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico</label>
              <input type="email" name="email" id="email" required value={formData.email} onChange={handleChange}
                     className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500 transition-colors" />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Tu Mensaje</label>
              <textarea name="message" id="message" rows="4" required value={formData.message} onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500 transition-colors" placeholder="Escribe tu consulta o solicitud para Vidriería Peña en Chiquinquirá..."></textarea>
            </div>
            <div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
              </Button>
            </div>
          </form>
          {submitMessage && (
            <div className={`mt-4 p-3 rounded-md text-sm ${submitMessage.type === 'success' ? 'bg-green-100 text-green-700' : submitMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'}`}>
              {submitMessage.text}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

// --- Main App Component ---
function App() {
  const [currentPage, setCurrentPage] = useState('Inicio');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const baseTitle = "Vidriería Peña | Chiquinquirá";
    if (currentPage === 'Inicio') {
      document.title = `${baseTitle} - Soluciones en Vidrio y Aluminio`;
    } else {
      document.title = `${currentPage} | ${baseTitle}`;
    }
  }, [currentPage]);

  const renderPage = () => {
    switch (currentPage) {
      case 'Inicio':
        return <HomePage setCurrentPage={setCurrentPage} />;
      case 'Servicios':
        return <ServicesPage />;
      case 'Productos':
        return <ProductsPage />;
      case 'Contacto':
        return <ContactPage />;
      default:
        return <HomePage setCurrentPage={setCurrentPage} />;
    }
  };

  const navItems = ['Inicio', 'Servicios', 'Productos', 'Contacto'];

  // Social Media Links - Replace # with your actual URLs
  const socialLinks = [
    { name: 'Facebook', href: 'https://www.facebook.com/profile.php?id=100093424883260', icon: <Facebook className="w-6 h-6" /> },
    //{ name: 'Instagram', href: '#', icon: <Instagram className="w-6 h-6" /> },
    //{ name: 'Twitter', href: '#', icon: <Twitter className="w-6 h-6" /> }, // Example, can be WhatsApp or other
    // { name: 'LinkedIn', href: '#', icon: <Linkedin className="w-6 h-6" /> },
    // { name: 'YouTube', href: '#', icon: <Youtube className="w-6 h-6" /> },
  ];


  return (
    <div className="min-h-screen bg-gray-50 font-sans antialiased">
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0">
              <a
                href="#inicio"
                onClick={(e) => { e.preventDefault(); setCurrentPage('Inicio'); }}
                className="flex items-center space-x-2 text-2xl font-bold text-sky-600 hover:text-sky-700 transition-colors"
              >
                <Home className="w-7 h-7" /> 
                <span>Vidriería Peña</span>
              </a>
            </div>
            <nav className="hidden md:flex space-x-2">
              {navItems.map(item => (
                <NavLink key={item} page={item} currentPage={currentPage} setCurrentPage={setCurrentPage}>
                  {item}
                </NavLink>
              ))}
            </nav>
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500"
              >
                <span className="sr-only">Abrir menú principal</span>
                {menuOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden absolute top-20 inset-x-0 bg-white shadow-lg rounded-b-lg p-4 z-40">
            <nav className="flex flex-col space-y-2">
              {navItems.map(item => (
                <NavLink key={item} page={item} currentPage={currentPage} setCurrentPage={setCurrentPage} setMenuOpen={setMenuOpen}>
                  {item}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main>
        {renderPage()}
      </main>

      <footer className="bg-gray-800 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 items-start">
            <div>
              <h5 className="font-semibold text-lg text-white mb-3">Vidriería Peña</h5>
              <p className="text-sm">Transformando espacios en Chiquinquirá con vidrio y aluminio de la más alta calidad.</p>
            </div>
            <div>
              <h5 className="font-semibold text-lg text-white mb-3">Enlaces Rápidos</h5>
              <ul className="space-y-1">
                {navItems.map(item => (
                  <li key={`footer-${item}`}>
                    <a 
                      href={`#${item.toLowerCase().replace(/\s+/g, '-')}`} 
                      onClick={(e) => {e.preventDefault(); setCurrentPage(item);}}
                      className="hover:text-sky-400 transition-colors text-sm"
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-lg text-white mb-3">Contacto en Chiquinquirá</h5>
              <p className="text-sm">vidalpechiquinquira@gmail.com</p>
              <p className="text-sm">(COL) 311 3503842</p>
              <p className="text-sm">calle 4b #7 110, Chiquinquirá, Boyacá</p>
            </div>
          </div>

          {/* Social Media Icons */}
          <div className="mb-8">
            <h5 className="font-semibold text-lg text-white mb-4">Síguenos en Redes</h5>
            <div className="flex justify-center space-x-6">
              {socialLinks.map(social => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank" // Open in new tab
                  rel="noopener noreferrer" // Security best practice
                  aria-label={social.name}
                  className="text-gray-400 hover:text-sky-400 transition-colors duration-200"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>
          
          <p className="text-sm border-t border-gray-700 pt-8">
            &copy; {new Date().getFullYear()} Vidriería Peña. Todos los derechos reservados. Servicios en Chiquinquirá y la región.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
